import { useAuth } from './auth/AuthContext';
import { useEffect } from 'react';
import { Spin, Typography, Flex, Button } from 'antd';

const { Text } = Typography;

const centerContainerStyle = { minHeight: '100vh' };

// Shared with Callback, so a failed sign-in says why instead of spinning.
export function AuthErrorView({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <Flex
      vertical
      justify="center"
      align="center"
      gap={16}
      style={centerContainerStyle}
      role="alert"
    >
      <Text style={{ fontSize: 16, fontWeight: 'bold' }} type="danger">
        Authentication Error
      </Text>
      <Text>{message}</Text>
      <Button type="primary" onClick={onRetry}>
        Try Again
      </Button>
    </Flex>
  );
}

export function RequireAuth({ children }: { children: React.ReactNode }) {
  const { authenticated, login, isLoading, authError, isLoggingOut } = useAuth();

  useEffect(() => {
    // Don't redirect if logging out - let the logout flow complete
    if (isLoggingOut) {
      return;
    }
    // Only redirect to login if not loading and not authenticated
    if (!isLoading && !authenticated && !authError) {
      login();
    }
  }, [authenticated, login, isLoading, authError, isLoggingOut]);

  // Show logging out message
  if (isLoggingOut) {
    return (
      <Flex
        vertical
        justify="center"
        align="center"
        gap={16}
        style={centerContainerStyle}
        role="status"
        aria-live="polite"
      >
        <Spin size="large" />
        <Text style={{ fontSize: 16 }}>Signing you out...</Text>
      </Flex>
    );
  }

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <Flex
        vertical
        justify="center"
        align="center"
        gap={16}
        style={centerContainerStyle}
        role="status"
        aria-live="polite"
      >
        <Spin size="large" />
        <Text style={{ fontSize: 16 }}>Loading...</Text>
      </Flex>
    );
  }

  // Show error state if authentication failed
  if (authError) {
    return <AuthErrorView message={authError} onRetry={login} />;
  }

  // Show redirecting message while not authenticated
  if (!authenticated) {
    return (
      <Flex
        vertical
        justify="center"
        align="center"
        gap={16}
        style={centerContainerStyle}
        role="status"
        aria-live="polite"
      >
        <Spin size="large" />
        <Text style={{ fontSize: 16 }}>Redirecting to sign in…</Text>
      </Flex>
    );
  }

  return <>{children}</>;
}
